// app/javascript/controllers/wistia_video_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nextVideoButton", "nextVideoOverlay"]

  static values = {
    code: String,
    session: String,
    node: String,
    lesson: String,
    program: String,
    nextVideoUrl: String,
    completed: Boolean,
    completionThreshold: Number
  }

  connect() {
    window._wq = window._wq || [];
    this.trackingBound = false;
    this.completedInSession = this.completedValue;

    this.bindToExistingWistiaPlayer();
    this.initWistia();
  }

  initWistia() {
    window._wq.push({
      id: this.codeValue,
      options: {
        plugin: {
          "captions-v1": { onByDefault: false, language: 'eng' }
        }
      },
      onReady: (video) => {
        this.handleVideoReady(video);
      }
    });
  }

  bindToExistingWistiaPlayer() {
    const existingVideo = window.Wistia?.api?.(this.codeValue);
    if (!existingVideo) return;

    this.handleVideoReady(existingVideo);
  }

  handleVideoReady(video) {
    if (this.trackingBound) return;

    this.trackingBound = true;
    this.bindTrackingEvents(video);
    this.hideNextVideoButton();
    this.dispatchLegacyEvent(video);
  }

  bindTrackingEvents(video) {
    video.bind("secondchange", (t) => {
      if (t % 10 === 0) this.saveActivity(video);
      this.completionReached(video);
    });

    video.bind('end', () => {
      this.saveActivity(video);
      this.toggleNextVideoButton(video);
    });

    video.bind('play', () => {
      this.hideNextVideoButton();
    });

    video.bind('pause', () => {
      this.saveActivity(video);
      this.toggleNextVideoButton(video);
    });
  }

  async saveActivity(video) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const timeData = video.secondsWatchedVector();
    const payload = {
      activity: {
        node: this.nodeValue,
        lesson: this.lessonValue,
        program_id: this.programValue,
        session: this.sessionValue,
        time_data: timeData
      }
    };

    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      await fetch('/activities', {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error("Failed to save activity:", error);
    }
  }

  dispatchLegacyEvent(video) {
    const event = new Event('OnVideoLoaded');
    document.addEventListener('OnVideoLoaded', (e) => {
      e.player = video;
    }, { once: true, capture: true });

    document.dispatchEvent(event);
  }

  showNextVideoButton() {
    if (!this.hasNextVideoButtonTarget) return;
    if (!this.hasNextVideoUrlValue || this.nextVideoUrlValue.length === 0) return;

    this.nextVideoButtonTarget.classList.remove("hidden");
    if (this.hasNextVideoOverlayTarget) this.nextVideoOverlayTarget.classList.remove("hidden");
  }

  hideNextVideoButton() {
    if (!this.hasNextVideoButtonTarget) return;

    this.nextVideoButtonTarget.classList.add("hidden");
    if (this.hasNextVideoOverlayTarget) this.nextVideoOverlayTarget.classList.add("hidden");
  }

  goToNextVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasNextVideoUrlValue || this.nextVideoUrlValue.length === 0) return;

    window.location.assign(this.nextVideoUrlValue);
  }

  toggleNextVideoButton(video) {
    if (this.completionReached(video)) {
      this.showNextVideoButton();
      return;
    }

    this.hideNextVideoButton();
  }

  completionReached(video) {
    if (this.completedInSession) return true;

    const currentProgressPercentage = this.timeDataProgressPercentage(video);
    if (currentProgressPercentage < this.completionThresholdValue) return false;

    this.completedInSession = true;
    return true;
  }

  timeDataProgressPercentage(video, timeData = video?.secondsWatchedVector?.() || []) {
    const lessonDuration = this.lessonDuration(video);
    if (lessonDuration <= 0) return 0;

    const watchedPositions = this.watchedPositionsCount(timeData, lessonDuration);
    return Math.round((watchedPositions / lessonDuration) * 100);
  }

  lessonDuration(video) {
    if (!video || !video.duration || video.duration() <= 0) return 0;

    return Math.floor(video.duration());
  }

  watchedPositionsCount(timeData, lessonDuration) {
    return timeData.slice(0, lessonDuration).filter((value) => Number(value) >= 1).length;
  }
}
